export const USER_ROLE = {
  ADMIN: 1,
  CONSULTANT: 3,
  PHOTOGRAPHER: 4,
  FINDER: 5,
} as const;

export type UserRoleId = (typeof USER_ROLE)[keyof typeof USER_ROLE];

export const USER_ROLE_LABELS: Record<UserRoleId, string> = {
  [USER_ROLE.ADMIN]: "Administrador",
  [USER_ROLE.CONSULTANT]: "Consultor",
  [USER_ROLE.PHOTOGRAPHER]: "Fotógrafo",
  [USER_ROLE.FINDER]: "Captador",
};

export const ASSIGNABLE_USER_ROLES: UserRoleId[] = [
  USER_ROLE.ADMIN,
  USER_ROLE.CONSULTANT,
  USER_ROLE.PHOTOGRAPHER,
  USER_ROLE.FINDER,
];

export type StaffUser = {
  id: number;
  first_name: string;
  email: string;
  phone: string;
};

export type StaffUsersByRole = {
  adms?: StaffUser[];
  consultants?: StaffUser[];
  photographers?: StaffUser[];
  finders?: StaffUser[];
};

export type StaffUserGroupKey = keyof StaffUsersByRole;

export type StaffUserWithRole = StaffUser & { roleId: UserRoleId };

export const STAFF_USER_GROUPS: { key: StaffUserGroupKey; roleId: UserRoleId }[] = [
  { key: "consultants", roleId: USER_ROLE.CONSULTANT },
  { key: "adms", roleId: USER_ROLE.ADMIN },
  { key: "photographers", roleId: USER_ROLE.PHOTOGRAPHER },
  { key: "finders", roleId: USER_ROLE.FINDER },
];

export function isAssignableUserRole(roleId: number): roleId is UserRoleId {
  return ASSIGNABLE_USER_ROLES.includes(roleId as UserRoleId);
}

export function getUserRoleLabel(roleId: number | undefined): string {
  if (roleId == null || !isAssignableUserRole(roleId)) {
    return "Desconhecido";
  }

  return USER_ROLE_LABELS[roleId];
}

export function roleRequiresCity(roleId: number | undefined): boolean {
  return roleId === USER_ROLE.CONSULTANT || roleId === USER_ROLE.PHOTOGRAPHER;
}

export function roleRequiresConsultantExtras(roleId: number | undefined): boolean {
  return roleId === USER_ROLE.CONSULTANT;
}

export function getAssignableRolesExcluding(currentRoleId: number): UserRoleId[] {
  return ASSIGNABLE_USER_ROLES.filter((roleId) => roleId !== currentRoleId);
}

export function flattenStaffUsers(users: StaffUsersByRole): StaffUserWithRole[] {
  return STAFF_USER_GROUPS.flatMap(({ key, roleId }) =>
    (users[key] ?? []).map((user) => ({ ...user, roleId }))
  );
}
